/**
 * Chart Comment Service
 *
 * Manages user comments/annotations on charts via NATS requests.
 * Comments can be tied to a specific timestamp or a shift (date + shift name).
 */

import { orgNatsClient, StatementIds } from './OrgNATSClient';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChartComment {
  id: string;
  /** Chart type identifier, e.g. "efficiency", "output", "motor_monitoring" */
  chart_type: string;
  /** Machine serial number (BK) */
  machine_bk: string;
  /** ISO timestamp the comment is anchored to (for time-based annotations) */
  timestamp?: string;
  /** Shift date in YYYY-MM-DD format (for shift-based annotations) */
  shift_date?: string;
  /** Shift name, e.g. "A", "B", "C" (for shift-based annotations) */
  shift_name?: string;
  /** The comment text */
  text: string;
  /** Username of the author */
  author: string;
  /** ISO timestamp when the comment was created */
  created_at: string;
  /** ISO timestamp when the comment was last updated */
  updated_at?: string;
}

export interface GetCommentsParams {
  chart_type: string;
  machine_bk: string;
  date_from: string;
  date_to: string;
}

export interface SaveCommentParams {
  chart_type: string;
  machine_bk: string;
  timestamp?: string;
  shift_date?: string;
  shift_name?: string;
  text: string;
  /** If set, updates an existing comment instead of creating a new one */
  comment_id?: string;
}

export interface DeleteCommentParams {
  comment_id: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

class ChartCommentService {
  /**
   * Fetch all comments for a given chart type, machine, and date range.
   */
  static async getComments(
    params: GetCommentsParams,
    token: string,
  ): Promise<ChartComment[]> {
    console.log('📝 ChartCommentService.getComments:', params);
    // Line charts (chart types "line_…") keep their comments under the line's
    // line_bk. PC.get_chart_comments checks serialno as a machine and so
    // refused every line; PC.get_line_chart_comments takes line_bk and lets a
    // comment through when the user may see a machine of the line.
    const lineChart = params.chart_type.startsWith('line_');
    const response = await orgNatsClient.request<ChartComment[]>(
      lineChart ? StatementIds.GET_LINE_CHART_COMMENTS : StatementIds.GET_CHART_COMMENTS,
      {
        chart_type: params.chart_type,
        ...(lineChart ? { line_bk: params.machine_bk } : { serialno: params.machine_bk }),
        date_from: params.date_from,
        date_to: params.date_to,
      },
      token,
    );
    console.log('📝 ChartCommentService.getComments response:', response);

    if (!response.success) {
      console.error('ChartCommentService: failed to fetch comments', response.error);
      return [];
    }

    // Map backend field names to our interface
    return (response.data ?? []).map((c: any) => ({
      id: c.comment_id || c.id,
      chart_type: c.chart_type,
      machine_bk: c.serialno || c.machine_bk,
      timestamp: c.timestamp && c.timestamp !== 0 ? c.timestamp : undefined,
      shift_date: c.shift_date,
      shift_name: c.shift_name,
      text: c.text,
      author: c.created_by || c.author,
      created_at: c.created_at,
      updated_at: c.updated_at,
    }));
  }

  /**
   * Save (create or update) a comment.
   */
  static async saveComment(
    params: SaveCommentParams,
    token: string,
  ): Promise<ChartComment | null> {
    const response = await orgNatsClient.request<ChartComment>(
      StatementIds.SAVE_CHART_COMMENT,
      {
        comment_id: params.comment_id,
        chart_type: params.chart_type,
        serialno: params.machine_bk,
        timestamp: params.timestamp,
        shift_date: params.shift_date,
        shift_name: params.shift_name,
        text: params.text,
      },
      token,
      { service: 'cockpitservice' },
    );

    if (!response.success) {
      console.error('ChartCommentService: failed to save comment', response.error);
      return null;
    }

    return response.data ?? null;
  }

  /**
   * Delete a comment by ID.
   */
  static async deleteComment(
    params: DeleteCommentParams,
    token: string,
  ): Promise<boolean> {
    const response = await orgNatsClient.request(
      StatementIds.DELETE_CHART_COMMENT,
      { comment_id: params.comment_id },
      token,
      { service: 'cockpitservice' },
    );

    if (!response.success) {
      console.error('ChartCommentService: failed to delete comment', response.error);
      return false;
    }

    return true;
  }
}

export default ChartCommentService;
